/**
 * Rule Toggle Policy (TV1-1696, #9)
 *
 * Enables/disables a single rule from the Reglas catalog. There is no dedicated
 * EnableRule command — toggling is done by re-issuing `backend.commands.SetRule`
 * with the FULL rule body and `enabled` flipped (ported 1:1 from the legacy
 * `rulesList/state/policies/toggleRulePolicy.ts`).
 *
 * Trigger mechanism: the CDSE policy engine reacts to STATE changes, not
 * dispatched commands. The user flips a row Switch, which dispatches the
 * user-sourced `ui/requestRuleToggle` (the submission trigger; allowlisted in
 * canApply per memory `feedback_cdse_submission_trigger_canapply`). That sets
 * `ui.pendingToggle` to a non-null value; this policy reacts to that
 * `null → non-null` transition.
 *
 * Flow on trigger:
 *   1. Find the full rule by `pendingToggle.ruleId` in `data.rules`.
 *   2. Build the SetRule body from that rule (strip read-only/computed fields),
 *      with `enabled` set to the requested value — matching the legacy call
 *      shape exactly (`{ id: entityId, body: { ...ruleBody, ruleId, enabled }}`).
 *   3. `await backend.commands.SetRule(...)`.
 *   4. Refetch the account rule set (same RULES_FILTER + RULES_SELECT as the
 *      shared rules query, incl. bypass rows) and dispatch `data/setRules` so the
 *      Switch reflects persisted backend truth (read-after-write, NO optimistic
 *      update).
 *   5. Always clear `pendingToggle` in `finally` so a failed toggle does not
 *      wedge the trigger.
 *
 * Errors are re-thrown to the global handler — no manual notifications. A
 * `closed` guard prevents late async writes after unmount.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { RulesReadmodelType } from '@bett3r-dev/teselly-domain';
import { RulesCatalogState } from '../state';
import { RulesCatalogCommand } from '../commands';

type Dispatch = (
  command: RulesCatalogCommand,
  meta: { source: string }
) => void;

/**
 * Read-only / computed properties stripped before sending a rule to SetRule.
 * Ported from the legacy `toggleRulePolicy` — these come from the readmodel /
 * rule definition and are not part of the writable command body.
 */
const EXCLUDED_PROPERTIES = [
  'accountId',
  'ruleId',
  'declaredInputs',
  'ruleDefinition',
  'hierarchy',
  'possibleTriggeringEvents'
] as const;

/**
 * Explicit `kind` clause: match both ordinary rules (`kind` absent) and bypass
 * rules. Without this the `/rules` route silently drops bypass rows (TV1-1648),
 * which would break the suppression calc in `resolveRulesForScope`. Mirrors the
 * shared rules query (`shared/state/policies/rulesQueryPolicy.ts`).
 */
const RULES_FILTER: NonNullable<
  Parameters<ClientLibraryType['queries']['Rules']>[0]
>['filter'] = [
  {
    OR: [
      { field: 'kind', op: 'eq', value: 'bypass' },
      { field: 'kind', op: 'notExists' }
    ]
  }
];

/**
 * Fields the catalog consumes (suppression calc + table rendering). Mirrors the
 * shared rules query select so the refetched set is shape-compatible with the
 * initial load.
 */
const RULES_SELECT: ( keyof RulesReadmodelType )[] = [
  'id',
  'name',
  'description',
  'ruleDefinition',
  'ruleDefinitionId',
  'ruleId',
  'kind',
  'targets',
  'enabled',
  'categories',
  'triggeringEvents',
  'hierarchy',
  'hierarchyLevel',
  'hierarchyEntityIds',
  'overrideGroups'
];

export const createRuleTogglePolicy = (
  backend: ClientLibraryType
): Policy<RulesCatalogState, RulesCatalogCommand> => {
  let closed = false;

  /** Refetch the full account rule set and push it into the store. */
  const refetchRules = async ( dispatch: Dispatch ) => {
    const rules = await backend.queries.Rules({
      filter: RULES_FILTER,
      select: RULES_SELECT
    });

    if ( closed ) return;

    dispatch(
      {
        type: 'automation/rulesCatalog/data/setRules',
        payload: { rules: rules || []}
      },
      { source: 'api' }
    );
  };

  /**
   * Re-set the full rule with `enabled` flipped, then refetch. Matches the
   * legacy SetRule call signature exactly: the entity id is the rule's first
   * hierarchy entity id (falling back to the rule's own id), and the body re-adds
   * `ruleId` (the rule id) plus the new `enabled` value.
   */
  const applyToggle = async (
    rule: RulesReadmodelType,
    enabled: boolean,
    dispatch: Dispatch
  ) => {
    // Strip read-only fields (legacy parity). The body is built loosely because
    // the readmodel type carries computed fields the command body omits.
    const ruleData = { ...rule } as Record<string, unknown>;
    EXCLUDED_PROPERTIES.forEach(( prop ) => {
      delete ruleData[prop];
    });

    // Entity identity for SetRule: first hierarchy entity id, else the rule id.
    const hierarchyEntityIds = rule.hierarchyEntityIds as string[] | undefined;
    const entityId = hierarchyEntityIds?.[0] || rule.id;

    const { id, name, ...ruleBody } = ruleData;

    await backend.commands.SetRule({
      id: entityId as string,
      body: {
        ...ruleBody,
        ruleId: id,
        enabled
      }
    } as Parameters<ClientLibraryType['commands']['SetRule']>[0]);

    // Read-after-write: refetch so the Switch reflects persisted state.
    await refetchRules( dispatch );
  };

  return {
    name: 'automation-rules-catalog-toggle',

    // React to the toggle trigger: pendingToggle null → non-null.
    shouldReact: ( prev, next ) =>
      prev.ui.pendingToggle === null && next.ui.pendingToggle !== null,

    onStateChange: async ({ next, ctx }) => {
      const pending = next.ui.pendingToggle;
      if ( !pending ) return;

      const { ruleId, enabled } = pending;

      try {
        const rule = next.data.rules.find(( r ) => r.id === ruleId );
        if ( rule ) {
          await applyToggle( rule, enabled, ctx.dispatch );
        }
      } finally {
        // Always clear the trigger so a failed toggle does not wedge.
        if ( !closed ) {
          ctx.dispatch(
            {
              type: 'automation/rulesCatalog/ui/clearPendingToggle',
              payload: {} as Record<string, never>
            },
            { source: 'api' }
          );
        }
      }
    },

    cleanup: () => {
      closed = true;
    }
  };
};
